// B2B Frontend - Rami-Care Branding: 2025-10-31
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Import Rami-Care brand styles
import './styles/brand.css';

// Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { SocketProvider } from './contexts/SocketContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { FeatureToggleProvider } from './contexts/FeatureToggleContext';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PublicCatalogPage from './pages/PublicCatalogPage';
import B2BNavbar from './components/B2BNavbar';
import HomeRedirect from './components/navigation/HomeRedirect';

// Field Agent Pages - B2B only
import AgentLayout from './components/agent/AgentLayout';
import AgentDashboard from './pages/agent/AgentDashboard';
import AgentCompaniesPage from './pages/agent/AgentCompaniesPage';
import AgentProductsPage from './pages/agent/AgentProductsPage';
import AgentCartPage from './pages/agent/AgentCartPage';
import AgentTargetsPage from './pages/agent/AgentTargetsPage';
import AgentOrdersPage from './pages/agent/AgentOrdersPage';
import AgentOrderDetailsPage from './pages/agent/AgentOrderDetailsPage';
import AgentProfilePage from './pages/agent/AgentProfilePage';
import AgentCreateOrderPage from './pages/agent/AgentCreateOrderPage';
import AgentPaymentsPage from './pages/agent/AgentPaymentsPage';
import CompanyDebtDetailsPage from './pages/agent/CompanyDebtDetailsPage';
import AgentNewPaymentPage from './pages/agent/AgentNewPaymentPage';
import AgentReturnsPage from './pages/agent/AgentReturnsPage';
import AgentNewReturnPage from './pages/agent/AgentNewReturnPage';
import AgentReturnDetailPage from './pages/agent/AgentReturnDetailPage';
import AgentWasteReport from './pages/agent/AgentWasteReport';
import AgentChecksPage from './pages/agent/AgentChecksPage';

// B2B Pages
import B2BProductsPage from './pages/b2b/B2BProductsPage';
import B2BFastCheckout from './pages/b2b/B2BFastCheckout';
import B2BOrdersPage from './pages/b2b/B2BOrdersPage';
import B2BOrderDetailPage from './pages/b2b/B2BOrderDetailPage';
import B2BPaymentsPage from './pages/b2b/B2BPaymentsPage';

// Seller Pages
import SellerBundlesDiscounts from './pages/seller/SellerBundlesDiscounts';

// Protected Route Component

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user: authUser, loading } = useAuth();

  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Get user from localStorage if AuthContext is still loading
  let user = authUser;
  if (loading || !authUser) {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        user = JSON.parse(storedUser);
      }
    } catch (e) {
      console.error('Failed to parse stored user:', e);
    }
  }

  // If still no user after checking localStorage, wait for auth to load
  if (!user && loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Always redirect field_agent to /agent if not already there
  const currentPath = window.location.pathname;
  if (user?.role === 'field_agent' && !currentPath.startsWith('/agent') && currentPath !== '/login') {
    return <Navigate to="/agent" replace />;
  }

  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  // Fix Android rotation issue (especially for Galaxy Tab A7)
  React.useEffect(() => {
    const handleOrientationChange = () => {
      // Method 1: Force viewport recalculation
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        const currentContent = viewport.getAttribute('content');
        // Temporarily change viewport
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0001');
        
        setTimeout(() => {
          // Restore original viewport
          viewport.setAttribute('content', currentContent);
          
          // Method 2: Force window resize event
          window.dispatchEvent(new Event('resize'));
          
          // Method 3: Trigger reflow (for Android tablets)
          document.documentElement.style.height = 'initial';
          setTimeout(() => {
            document.documentElement.style.height = '';
          }, 20);
        }, 100);
      }
      
      // Method 4: Force full repaint (aggressive for stubborn Android devices)
      const root = document.getElementById('root');
      if (root) {
        root.style.opacity = '0.99';
        setTimeout(() => {
          root.style.opacity = '';
        }, 50);
      }
    };

    // Listen to multiple events for better Android compatibility
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    // For Android Chrome/Samsung Internet
    if (window.screen && window.screen.orientation) {
      window.screen.orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
      if (window.screen && window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);

  return (
    <AuthProvider>
      <FeatureToggleProvider>
        <AnalyticsProvider>
          <SocketProvider>
            <CartProvider>
              <Router>
            <div className="App">
              <Toaster
                position="top-center"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#10B981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 4000,
                    iconTheme: {
                      primary: '#EF4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />

              <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/catalog" element={<PublicCatalogPage />} />
          <Route path="/open-catalog" element={<Navigate to="/catalog" replace />} />

          {/* Field Agent Routes - B2B ONLY */}
          <Route path="/agent" element={
            <ProtectedRoute requiredRole="field_agent">
              <AgentLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AgentDashboard />} />
            <Route path="companies" element={<AgentCompaniesPage />} />
            <Route path="products" element={<AgentProductsPage />} />
            <Route path="cart/:companyId" element={<AgentCartPage />} />
            <Route path="create-order" element={<AgentCreateOrderPage />} />
            <Route path="create-order/:companyId" element={<AgentCreateOrderPage />} />
            <Route path="targets" element={<AgentTargetsPage />} />
            <Route path="orders" element={<AgentOrdersPage />} />
            <Route path="orders/:orderId" element={<AgentOrderDetailsPage />} />
            <Route path="payments" element={<AgentPaymentsPage />} />
            <Route path="payments/:companyId" element={<CompanyDebtDetailsPage />} />
            <Route path="payments/:companyId/new" element={<AgentNewPaymentPage />} />
            <Route path="checks" element={<AgentChecksPage />} />
            <Route path="returns" element={<AgentReturnsPage />} />
            <Route path="returns/new" element={<AgentNewReturnPage />} />
            <Route path="returns/:id" element={<AgentReturnDetailPage />} />
            <Route path="bundles-discounts" element={<SellerBundlesDiscounts />} />
            <Route path="waste-report" element={<AgentWasteReport />} />
            <Route path="profile" element={<AgentProfilePage />} />
          </Route>
          
          {/* B2B Customer Routes */}
          <Route path="/trade" element={
            <ProtectedRoute requiredRole="b2b">
              <>
                <B2BNavbar />
                <B2BProductsPage />
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/orders/:orderId" element={
            <ProtectedRoute requiredRole="b2b">
              <>
                <B2BNavbar />
                <B2BOrderDetailPage />
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/orders" element={
            <ProtectedRoute requiredRole="b2b">
              <>
                <B2BNavbar />
                <B2BOrdersPage />
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/payments" element={
            <ProtectedRoute requiredRole="b2b">
              <>
                <B2BNavbar />
                <B2BPaymentsPage />
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/fast-checkout" element={
            <ProtectedRoute requiredRole="b2b">
              <>
                <B2BNavbar />
                <B2BFastCheckout />
              </>
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomeRedirect />
              </ProtectedRoute>
            }
          />

          {/* Dashboard for B2B customers */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredRole="b2b">
                <>
                  <B2BNavbar />
                  <DashboardPage />
                </>
              </ProtectedRoute>
            }
          />

          {/* Redirect any unknown paths to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
              </Router>
            </CartProvider>
          </SocketProvider>
        </AnalyticsProvider>
      </FeatureToggleProvider>
    </AuthProvider>
  );
}

export default App;
